import React, { useState, useEffect } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../contexts/ToastContext';
import permissionsAdminService from '../../services/permissionsAdminService';
import { Shield, Save, RefreshCw, X, ChevronDown, ChevronUp } from 'lucide-react';
import './PermissionsAdminPage.css';

const PermissionsAdminPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [permissionsByRole, setPermissionsByRole] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedRoleCode, setSelectedRoleCode] = useState('');

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const data = await permissionsAdminService.getPermissionsByRole();
      setPermissionsByRole(data);
      setChanges({});

      const roleCodes = Object.keys(data);
      setSelectedRoleCode(prev => (roleCodes.includes(prev) ? prev : (roleCodes[0] || '')));
      
      // Open all sections by default.
      const initialExpanded = {};
      Object.values(data).forEach(roleData => {
        roleData.permissions.forEach(permission => {
          initialExpanded[permission.resource] = true;
        });
      });
      setExpandedSections(initialExpanded);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error(t('permissionsAdmin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (resource) => {
    setExpandedSections(prev => ({
      ...prev,
      [resource]: !prev[resource]
    }));
  };

  const handleTogglePermission = (permission) => {
    setChanges(prev => {
      const hasPendingChange = Object.prototype.hasOwnProperty.call(prev, permission.id);
      const currentValue = hasPendingChange ? prev[permission.id] : permission.allowed;
      const nextValue = !currentValue;
      const nextChanges = { ...prev };

      if (nextValue === permission.allowed) {
        delete nextChanges[permission.id];
      } else {
        nextChanges[permission.id] = nextValue;
      }

      return nextChanges;
    });
  };

  const handleSaveChanges = async () => {
    if (Object.keys(changes).length === 0) {
      toast.info(t('permissionsAdmin.noChanges'));
      return;
    }

    try {
      setSaving(true);
      const updates = Object.entries(changes).map(([permissionId, allowed]) => ({
        permission_id: parseInt(permissionId),
        allowed
      }));

      await permissionsAdminService.bulkUpdate(updates);
      toast.success(t('permissionsAdmin.saveSuccess', { count: updates.length }));
      await fetchPermissions();
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error(t('permissionsAdmin.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetChanges = () => {
    setChanges({});
    toast.info(t('permissionsAdmin.reset'));
  };

  const getPermissionValue = (permission) => {
    if (Object.prototype.hasOwnProperty.call(changes, permission.id)) {
      return changes[permission.id];
    }
    return permission.allowed;
  };

  const hasChanges = Object.keys(changes).length > 0;

  const getResourceLabel = (resource) => {
    const labels = {
      'sessions': t('permissionsAdmin.resources.sessions'),
      'formations': t('permissionsAdmin.resources.formations'),
      'consommables': t('permissionsAdmin.resources.consommables'),
      'stock': t('permissionsAdmin.resources.stock'),
      'instructors': t('permissionsAdmin.resources.instructors'),
      'demandes': t('permissionsAdmin.resources.demandes'),
      'fournisseurs': t('permissionsAdmin.resources.fournisseurs'),
      'centres': t('permissionsAdmin.resources.centres'),
      'comptabilite': t('permissionsAdmin.resources.comptabilite'),
      'users': t('permissionsAdmin.resources.users'),
      'permissions': t('permissionsAdmin.resources.permissions'),
      'action_items': t('permissionsAdmin.resources.actionItems')
    };
    return labels[resource] || resource;
  };

  const getActionLabel = (action) => {
    const labels = {
      'display': t('permissionsAdmin.actions.display'),
      'view': t('permissionsAdmin.actions.view'),
      'request': t('permissionsAdmin.actions.request'),
      'create': t('permissionsAdmin.actions.create'),
      'edit': t('permissionsAdmin.actions.edit'),
      'delete': t('permissionsAdmin.actions.delete'),
      'download': t('permissionsAdmin.actions.download'),
      'validate': t('permissionsAdmin.actions.validate'),
      'reject': t('permissionsAdmin.actions.reject')
    };
    return labels[action] || action;
  };

  const leftColumnActions = ['create', 'delete', 'display', 'download', 'edit'];
  const rightColumnActions = ['reject', 'request', 'validate', 'view'];

  const splitPermissionsByColumns = (items) => {
    const left = [];
    const right = [];

    items.forEach((permission) => {
      if (leftColumnActions.includes(permission.action)) {
        left.push(permission);
        return;
      }

      if (rightColumnActions.includes(permission.action)) {
        right.push(permission);
        return;
      }

      left.push(permission);
    });

    left.sort((a, b) => leftColumnActions.indexOf(a.action) - leftColumnActions.indexOf(b.action));
    right.sort((a, b) => rightColumnActions.indexOf(a.action) - rightColumnActions.indexOf(b.action));

    return { left, right };
  };

  const roleEntries = Object.entries(permissionsByRole);
  const selectedRoleData = selectedRoleCode ? permissionsByRole[selectedRoleCode] : null;

  // Group selected role permissions by resource.
  const groupedByResource = {};
  (selectedRoleData?.permissions || []).forEach(permission => {
    if (!groupedByResource[permission.resource]) {
      groupedByResource[permission.resource] = [];
    }
    groupedByResource[permission.resource].push(permission);
  });

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('permissionsAdmin.loading')}</span>
      </div>
    );
  }

  return (
    <div className="permissions-admin-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Shield size={32} />
            {t('permissionsAdmin.title')}
          </h1>
          <p className="page-subtitle">
            {t('permissionsAdmin.subtitle')}
          </p>
        </div>
        <div className="header-actions">
          {hasChanges && (
            <>
              <button 
                onClick={handleResetChanges} 
                className="btn-secondary"
                disabled={saving}
              >
                <X size={20} />
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleSaveChanges} 
                className="btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <RefreshCw size={20} className="spinning" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    {t('permissionsAdmin.save', { count: Object.keys(changes).length })}
                  </>
                )}
              </button>
            </>
          )}
          <button 
            onClick={fetchPermissions} 
            className="btn-secondary"
            disabled={loading || saving}
          >
            <RefreshCw size={20} />
            {t('permissionsAdmin.refresh')}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="changes-banner">
          <span>
            {t('permissionsAdmin.changesPending', { count: Object.keys(changes).length })}
          </span>
        </div>
      )}

      <div className="role-selector-bar">
        <div className="role-tabs" role="tablist" aria-label={t('permissionsAdmin.rolesAria')}>
          {roleEntries.map(([roleCode, roleData]) => (
            <button
              key={roleCode}
              type="button"
              className={`role-tab ${selectedRoleCode === roleCode ? 'active' : ''}`}
              onClick={() => setSelectedRoleCode(roleCode)}
            >
              {roleData.name}
            </button>
          ))}
        </div>

        <select
          className="role-select-mobile"
          value={selectedRoleCode}
          onChange={(e) => setSelectedRoleCode(e.target.value)}
          aria-label={t('permissionsAdmin.selectRoleAria')}
        >
          {roleEntries.map(([roleCode, roleData]) => (
            <option key={roleCode} value={roleCode}>
              {roleData.name} ({roleCode})
            </option>
          ))}
        </select>
      </div>

      <div className="permissions-table-container">
        {Object.entries(groupedByResource).map(([resource, items]) => {
          const isExpanded = expandedSections[resource];
          const { left, right } = splitPermissionsByColumns(items);
          
          return (
            <div key={resource} className="resource-table-section">
              <div 
                className="resource-table-header"
                onClick={() => toggleSection(resource)}
              >
                <h2 className="resource-table-title">{getResourceLabel(resource)}</h2>
                <button className="dropdown-toggle" type="button">
                  {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </button>
              </div>
              
              {isExpanded && (
                isMobile ? (
                  <div className="permissions-actions-two-columns">
                    <div className="permissions-action-column">
                      {left.map((permission) => {
                        const isAllowed = getPermissionValue(permission);
                        const hasChange = Object.prototype.hasOwnProperty.call(changes, permission.id);

                        return (
                          <div key={permission.id} className="permission-action-item">
                            <div className="action-cell">
                              <strong>{getActionLabel(permission.action)}</strong>
                            </div>
                            <div className={`checkbox-cell ${hasChange ? 'has-change' : ''}`}>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => handleTogglePermission(permission)}
                                  className="permission-checkbox"
                                />
                                <span className="checkbox-custom"></span>
                                <span className="permission-status">
                                  {isAllowed ? t('permissionsAdmin.allowed') : t('permissionsAdmin.denied')}
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="permissions-action-column">
                      {right.map((permission) => {
                        const isAllowed = getPermissionValue(permission);
                        const hasChange = Object.prototype.hasOwnProperty.call(changes, permission.id);

                        return (
                          <div key={permission.id} className="permission-action-item">
                            <div className="action-cell">
                              <strong>{getActionLabel(permission.action)}</strong>
                            </div>
                            <div className={`checkbox-cell ${hasChange ? 'has-change' : ''}`}>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => handleTogglePermission(permission)}
                                  className="permission-checkbox"
                                />
                                <span className="checkbox-custom"></span>
                                <span className="permission-status">
                                  {isAllowed ? t('permissionsAdmin.allowed') : t('permissionsAdmin.denied')}
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th className="action-column">{t('permissionsAdmin.columns.action')}</th>
                        <th className="access-column">{t('permissionsAdmin.columns.access')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((permission) => {
                        const isAllowed = getPermissionValue(permission);
                        const hasChange = Object.prototype.hasOwnProperty.call(changes, permission.id);

                        return (
                          <tr key={permission.id}>
                            <td className="action-cell">
                              <strong>{getActionLabel(permission.action)}</strong>
                            </td>
                            <td className={`checkbox-cell ${hasChange ? 'has-change' : ''}`}>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isAllowed}
                                  onChange={() => handleTogglePermission(permission)}
                                  className="permission-checkbox"
                                />
                                <span className="checkbox-custom"></span>
                                <span className="permission-status">
                                  {isAllowed ? t('permissionsAdmin.allowed') : t('permissionsAdmin.denied')}
                                </span>
                              </label>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionsAdminPage;
